import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  BarChart3, Users, GraduationCap, Activity, TrendingUp,
  Building2, Crown, Loader2, RefreshCw, Calendar,
  ChevronUp, ChevronDown, Minus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple bar chart component
const SimpleBarChart = ({ data, height = 200 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        Sin datos disponibles
      </div>
    );
  }
  
  const maxValue = Math.max(...data.map(d => d.count || d.value || 0));
  
  return (
    <div className="flex items-end justify-between gap-2" style={{ height }}>
      {data.map((item, index) => {
        const value = item.count || item.value || 0;
        const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
        
        return (
          <div key={index} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-xs font-medium text-gray-600">{value}</span>
            <div 
              className="w-full bg-blue-500 rounded-t transition-all duration-500"
              style={{ height: `${Math.max(percentage, 5)}%` }}
            />
            <span className="text-xs text-gray-500 truncate w-full text-center">
              {item.month || item.name || item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Stat card with trend
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600'
  };
  
  const TrendIcon = trend > 0 ? ChevronUp : trend < 0 ? ChevronDown : Minus;
  const trendColor = trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-gray-400';
  
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-bold">{value}</p>
              {trend !== undefined && (
                <span className={`flex items-center text-xs ${trendColor}`}>
                  <TrendIcon className="w-3 h-3" />
                  {Math.abs(trend)}%
                </span>
              )}
            </div>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Usage meter component
const UsageMeter = ({ label, current, max, unlimited }) => {
  if (unlimited) {
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">{label}</span>
          <span className="text-green-600 font-medium">Ilimitado</span>
        </div>
        <Progress value={0} className="h-2" />
      </div>
    );
  }
  
  const percentage = max > 0 ? (current / max) * 100 : 0;
  const isWarning = percentage >= 80;
  const isCritical = percentage >= 95;
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className={`font-medium ${isCritical ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-gray-900'}`}>
          {current} / {max}
        </span>
      </div>
      <Progress 
        value={percentage} 
        className="h-2"
        indicatorClassName={isCritical ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : 'bg-blue-500'}
      />
    </div>
  );
};

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('6');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !['super_admin', 'admin'].includes(user.role)) {
      toast.error('Acceso denegado');
      navigate('/app/dashboard');
      return;
    }
    
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/institution?months=${period}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        toast.error('Error al cargar analytics');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  const { users, simulations, courses, scenarios, plan_usage } = analytics || {};

  return (
    <>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-blue-600" />
              Analytics
            </h1>
            <p className="text-gray-500 mt-1">
              {analytics?.institution_name || 'Institución'}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Último mes</SelectItem>
                <SelectItem value="3">Últimos 3 meses</SelectItem>
                <SelectItem value="6">Últimos 6 meses</SelectItem>
                <SelectItem value="12">Último año</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Usuarios Totales"
            value={users?.total || 0}
            subtitle={`${users?.new_this_month || 0} nuevos este mes`}
            icon={Users}
            color="blue"
          />
          <StatCard
            title="Simulaciones Completadas"
            value={simulations?.completed || 0}
            subtitle={`${simulations?.total || 0} totales`}
            icon={Activity}
            color="green"
          />
          <StatCard
            title="Cursos Activos"
            value={courses?.active || 0}
            subtitle={`${courses?.students_enrolled || 0} estudiantes inscritos`}
            icon={GraduationCap}
            color="purple"
          />
          <StatCard
            title="Puntaje Promedio"
            value={`${simulations?.average_score || 0}%`}
            subtitle="En simulaciones completadas"
            icon={TrendingUp}
            color="orange"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Simulations by Month */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Simulaciones por Mes</CardTitle>
              <CardDescription>Actividad de simulación en el período</CardDescription>
            </CardHeader>
            <CardContent>
              <SimpleBarChart 
                data={simulations?.by_month || []} 
                height={200}
              />
            </CardContent>
          </Card>

          {/* Users by Role */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Usuarios por Rol</CardTitle>
              <CardDescription>Distribución de usuarios</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(users?.by_role || {}).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        role === 'admin' ? 'bg-purple-500' :
                        role === 'instructor' ? 'bg-blue-500' :
                        role === 'alumno' ? 'bg-green-500' :
                        'bg-gray-500'
                      }`} />
                      <span className="text-sm capitalize">{role}</span>
                    </div>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
                {Object.keys(users?.by_role || {}).length === 0 && (
                  <p className="text-gray-400 text-center py-4">Sin datos</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scenarios and Plan Usage Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Most Used Scenarios */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Escenarios Más Usados</CardTitle>
              <CardDescription>Top 5 escenarios por uso</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(scenarios?.most_used || []).map((scenario, index) => (
                  <div key={scenario.scenario_id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-gray-100 text-gray-700' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-50 text-gray-500'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{scenario.name}</p>
                      <p className="text-xs text-gray-500">{scenario.category}</p>
                    </div>
                    <Badge variant="outline">{scenario.count} usos</Badge>
                  </div>
                ))}
                {(scenarios?.most_used || []).length === 0 && (
                  <p className="text-gray-400 text-center py-4">Sin datos de uso</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Plan Usage */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Uso del Plan</CardTitle>
                  <CardDescription>
                    {plan_usage?.plan_name || 'Sin plan activo'}
                  </CardDescription>
                </div>
                {plan_usage && (
                  <Badge className="bg-blue-100 text-blue-700">
                    <Crown className="w-3 h-3 mr-1" />
                    {Math.round(plan_usage.overall_usage_percentage)}% usado
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {plan_usage ? (
                <div className="space-y-4">
                  {Object.entries(plan_usage.limits || {}).map(([key, limit]) => (
                    <UsageMeter
                      key={key}
                      label={key.replace(/_/g, ' ').replace('max ', '').replace(/^\w/, c => c.toUpperCase())}
                      current={limit.current}
                      max={limit.max}
                      unlimited={limit.unlimited}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay suscripción activa</p>
                  <Button variant="outline" className="mt-4" onClick={() => navigate('/#planes')}>
                    Ver Planes
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-blue-600">{scenarios?.total_available || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Escenarios Disponibles</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-green-600">
                  {courses?.average_students_per_course?.toFixed(1) || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">Promedio Alumnos/Curso</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-purple-600">
                  {simulations?.in_progress || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">Simulaciones en Progreso</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AnalyticsPage;
